import React, { useState, useEffect } from 'react';
import { Link } from './Link';
import { Logo } from './Logo';
import { createCheckoutSession } from '../services/billingService';
import { getAppConfig, verifySession } from '../services/authService';
import { useRouter } from './Router';
import { isNativeApp } from '../utils/platform';
import { 
    Check, X, RefreshCw, Zap, ArrowRight, 
    HelpCircle, ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';

interface Plan {
    name: string;
    badge?: string;
    monthlyPriceUSD: number;
    annualPriceUSD: number;
    monthlyPriceNGN: number;
    annualPriceNGN: number;
    description: string;
    features: string[];
    missingFeatures?: string[];
    planCodeEnv: 'starterPlanCode' | 'proPlanCode' | 'advancedPlanCode';
    isPopular: boolean;
    cta: string;
}

const plans: Plan[] = [
    {
        name: 'Starter',
        monthlyPriceUSD: 29,
        annualPriceUSD: 24,
        monthlyPriceNGN: 45000,
        annualPriceNGN: 36000,
        description: 'For growing businesses initiating broadcast campaigns and verified customer messaging.',
        features: [
            '10,000 Messages/month included',
            'Official Meta Cloud API channel',
            'Manual bulk campaigns with CSV import',
            'Unlimited contact storage & tagging',
            'Approved Meta interactive templates',
            'Basic webhook event stream',
            'Standard SLA (email response < 12h)'
        ],
        missingFeatures: [
            'Automated 2-way Google Sheets sync',
            'AI Agent visual canvas (latest Gemini models)',
            'Multi-operator team inbox & live takeover'
        ],
        planCodeEnv: 'starterPlanCode',
        isPopular: false,
        cta: 'Deploy Starter'
    },
    {
        name: 'Pro',
        badge: 'Recommended for Growth',
        monthlyPriceUSD: 79,
        annualPriceUSD: 64,
        monthlyPriceNGN: 110000,
        annualPriceNGN: 88000,
        description: 'Automate sales funnels, qualification, and support with autonomous AI agents and Sheets sync.',
        features: [
            '50,000 Messages/month included',
            'Official Meta Cloud API channel',
            'Bidirectional Google Sheets real-time sync',
            'Visual AI Agent builder with latest Gemini models',
            'Knowledge Base RAG (PDF & text uploads)',
            'Autonomous customer qualification & triage',
            'Custom business logic branching nodes',
            'Priority SLA (< 2h support triage)'
        ],
        missingFeatures: [
            'Multi-agent role-based team inbox',
            'Custom dedicated webhook proxies'
        ],
        planCodeEnv: 'proPlanCode',
        isPopular: true,
        cta: 'Deploy Pro'
    },
    {
        name: 'Advanced',
        badge: 'Enterprise Architecture',
        monthlyPriceUSD: 199,
        annualPriceUSD: 159,
        monthlyPriceNGN: 280000,
        annualPriceNGN: 224000,
        description: 'For high-velocity operations requiring complex multi-agent logic, CRM webhooks, and team routing.',
        features: [
            '250,000 Messages/month included',
            'Official Meta Cloud API channel',
            'Real-time Sheets sync & webhooks pipeline',
            'Advanced AI Agent reasoning with latest Gemini models',
            'Custom JavaScript logic execution nodes',
            'Multi-operator team inbox with live takeover',
            'Unlimited team seats & role-based permissions',
            'Dedicated success architect & 99.99% uptime SLA'
        ],
        planCodeEnv: 'advancedPlanCode',
        isPopular: false,
        cta: 'Deploy Advanced'
    }
];

const COMPARISON_ROWS = [
    { feature: 'Official Meta Cloud API (Zero Ban Risk)', starter: 'Included', pro: 'Included', advanced: 'Included' },
    { feature: 'Monthly Included Messages', starter: '10,000', pro: '50,000', advanced: '250,000' },
    { feature: 'Bidirectional Google Sheets Sync', starter: 'Manual CSV', pro: 'Real-time Continuous', advanced: 'Real-time Continuous' },
    { feature: 'AI Reasoning Engine', starter: 'Standard Templates', pro: 'Latest Gemini Models', advanced: 'Latest Gemini Models + Custom Logic' },
    { feature: 'Knowledge Base RAG Documents', starter: '—', pro: 'Up to 25 Docs', advanced: 'Unlimited' },
    { feature: 'Team Shared Inbox Seats', starter: '1 Seat', pro: '2 Seats', advanced: 'Unlimited Seats' },
    { feature: 'Outbound Rate Throttling & Queue Guard', starter: 'Standard', pro: 'Accelerated', advanced: 'Dedicated High-Throughput' },
    { feature: 'Custom Script & API Nodes', starter: '—', pro: 'Standard HTTP', advanced: 'Full JS Sandbox & Webhooks' },
    { feature: 'Support Level', starter: 'Email (12h)', pro: 'Priority (< 2h)', advanced: 'Dedicated Engineer & Slack SLA' }
];

const FAQS = [
    {
        q: "How does billing work with official Meta WhatsApp conversation fees?",
        a: "Bytecohort Reach provides the software engine, AI orchestration, Google Sheets synchronization, and campaign queuing. Meta bills WhatsApp conversation charges directly to your linked Meta Business Manager account at official wholesale rates (usually less than $0.005 to $0.04 per conversation depending on country and conversation category)."
    },
    {
        q: "Can my WhatsApp number get banned for high-volume broadcasts?",
        a: "No. Unlike unofficial scraping tools or WhatsApp Web automation extensions that violate Meta's Terms of Service and trigger sudden bans, Bytecohort Reach connects exclusively through the official Meta Graph Cloud API. Your sender number maintains verified business standing, cryptographic webhook security, and high tier deliverability."
    },
    {
        q: "How are the latest Gemini models integrated into the AI agent builder?",
        a: "Every inbound message routed to an AI node is evaluated by the latest Gemini models. The model uses your knowledge base documents (RAG) and strict system instructions to classify intent, extract parameters (e.g. order numbers or booking times), and trigger business logic without human hallucination."
    },
    {
        q: "Can I pay in Nigerian Naira (NGN) as well as USD?",
        a: "Yes. We support local and international payment rails via Paystack and Stripe. Nigerian entities can subscribe directly in NGN via card, bank transfer, or USSD with immediate receipt and automatic plan provisioning."
    },
    {
        q: "What happens if our organization exceeds the included monthly message quota?",
        a: "Your system will never cut off live inbound customer conversations. Additional message dispatches are billed at a flat rate of $0.002 per message (or local equivalent), or you can easily upgrade to the next tier directly from your billing console."
    }
];

export default function PricingPage() {
    const [planCodes, setPlanCodes] = useState<Record<string, string> | null>(null);
    const [isLoading, setIsLoading] = useState<string | null>(null);
    const [isRestoring, setIsRestoring] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currency, setCurrency] = useState<'USD' | 'NGN'>('USD');
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
    const [expandedFaq, setExpandedFaq] = useState<number | null>(0);
    const { navigate } = useRouter();
    
    const isMobileEnv = isNativeApp();

    useEffect(() => {
        const fetchPlanCodes = async () => {
            try {
                const config = await getAppConfig();
                setPlanCodes(config.paystack);
            } catch (err) {
                console.error("Failed to fetch plan codes:", err);
                setError("Could not load pricing plans. Please try again later.");
            }
        };
        fetchPlanCodes();
    }, []);

    const handleSubscribe = async (plan: Plan) => {
        const authToken = localStorage.getItem('whatsapp_sender_token');
        if (!authToken) {
            navigate('/app?redirect=pricing');
            return;
        }

        if (!planCodes) {
            setError("Pricing plans are not available at the moment.");
            return;
        }
        
        const planCode = planCodes[plan.planCodeEnv];
        if (!planCode || planCode.includes('PLACEHOLDER')) {
            setError(`This plan has not been configured in the backend yet.`);
            return;
        }

        const isAnnual = billingCycle === 'annual';
        const priceNum = currency === 'USD' 
            ? (isAnnual ? plan.annualPriceUSD : plan.monthlyPriceUSD)
            : (isAnnual ? plan.annualPriceNGN : plan.monthlyPriceNGN);
        const amountInSubunits = Math.round(priceNum * 100);

        setIsLoading(planCode);
        setError(null);
        try {
            const { authorization_url } = await createCheckoutSession(planCode, amountInSubunits);
            window.location.href = authorization_url;
        } catch (err: any) {
            setError(err.message || 'Failed to initiate payment. Please try again.');
            setIsLoading(null);
        }
    };

    const handleRestorePurchases = async () => {
        setIsRestoring(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('whatsapp_sender_token');
            if (!authToken) {
                navigate('/app?redirect=pricing');
                return;
            }
            await verifySession();
            alert("Account synchronized. If you have an active subscription, your features should now be unlocked.");
            navigate('/app/account');
        } catch (err: any) {
            console.error("Restore error:", err);
            setError("Failed to restore purchases. Please check your connection.");
        } finally {
            setIsRestoring(false);
        }
    };

    const toggleFaq = (idx: number) => {
        setExpandedFaq(expandedFaq === idx ? null : idx);
    };

    return (
        <div className="min-h-screen bg-[#07080B] text-zinc-100 font-sans antialiased selection:bg-emerald-500 selection:text-black flex flex-col relative overflow-x-hidden">
            {/* Atmospheric Ambient Light */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/[0.04] blur-[140px] rounded-full pointer-events-none" />
                <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-cyan-500/[0.03] blur-[150px] rounded-full pointer-events-none" />
            </div>

            {/* Live Operational Status Bar */}
            <div className="relative z-50 w-full bg-[#0B0D14] border-b border-white/[0.06] py-1.5 px-4 text-[11px] font-mono text-zinc-400">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1.5 text-emerald-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            Official Meta Graph API v21.0 Online
                        </span>
                        <span className="hidden sm:inline text-zinc-600">•</span>
                        <span className="hidden sm:inline">Paystack & Stripe Gateways Active</span>
                        <span className="hidden md:inline text-zinc-600">•</span>
                        <span className="hidden md:inline">Intelligence: Latest Gemini Models</span>
                    </div>
                    <div className="flex items-center gap-4 text-zinc-400">
                        <span className="hidden lg:inline">SOC-2 Type II Compliant</span>
                        <span className="text-zinc-500">Tier 3 Delivery Health</span>
                    </div>
                </div>
            </div>

            {/* Architectural Navigation Bar */}
            <header className="sticky top-0 z-40 bg-[#07080B]/85 backdrop-blur-md border-b border-white/[0.06]">
                <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <Link href="/" className="flex items-center space-x-2">
                            <Logo className="h-8 w-auto" />
                        </Link>
                        <div className="hidden md:flex items-center gap-6 text-xs font-medium text-zinc-400">
                            <Link href="/" className="hover:text-white transition-colors">Platform</Link>
                            <Link href="/pricing" className="text-white font-semibold flex items-center gap-1">
                                Pricing
                                <span className="w-1 h-1 rounded-full bg-emerald-400" />
                            </Link>
                            <Link href="/docs" className="hover:text-white transition-colors">Docs</Link>
                            <Link href="/blog" className="hover:text-white transition-colors">Playbooks</Link>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Link 
                            href="/app" 
                            className="text-xs font-medium text-zinc-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors"
                        >
                            Sign In
                        </Link>
                        <Link 
                            href="/app" 
                            className="text-xs font-semibold text-zinc-950 bg-white hover:bg-zinc-200 px-4 py-2 rounded-lg transition-all shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2)] flex items-center gap-1.5"
                        >
                            <span>Launch Console</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </nav>
            </header>

            {/* Main Pricing Hero & Plan Section */}
            <main className="flex-1 relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 w-full">
                
                {/* Header Copy */}
                <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-mono uppercase tracking-wider mb-6">
                        <Zap className="w-3.5 h-3.5" />
                        Predictable Infrastructure Pricing
                    </div>
                    
                    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Engineered for high-volume WhatsApp automation.
                    </h1>
                    
                    <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto">
                        Transparent plans designed for modern growth teams and enterprise developers. Zero hidden seat costs. Scale autonomously with the latest Gemini models and official Meta Cloud deliverability.
                    </p>

                    {/* Dual Control: Currency Selector & Annual Billing Toggle */}
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        {/* Currency Selector */}
                        {!isMobileEnv && (
                            <div className="bg-[#0B0D14] p-1 rounded-xl border border-white/[0.08] flex relative shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                                <button 
                                    onClick={() => setCurrency('USD')}
                                    className={`px-4 py-1.5 rounded-lg text-xs font-mono font-medium transition-all z-10 ${
                                        currency === 'USD' ? 'bg-white text-zinc-950 font-semibold shadow-sm' : 'text-zinc-400 hover:text-white'
                                    }`}
                                >
                                    USD ($)
                                </button>
                                <button 
                                    onClick={() => setCurrency('NGN')}
                                    className={`px-4 py-1.5 rounded-lg text-xs font-mono font-medium transition-all z-10 ${
                                        currency === 'NGN' ? 'bg-white text-zinc-950 font-semibold shadow-sm' : 'text-zinc-400 hover:text-white'
                                    }`}
                                >
                                    NGN (₦)
                                </button>
                            </div>
                        )}

                        {/* Billing Period Toggle */}
                        <div className="bg-[#0B0D14] p-1 rounded-xl border border-white/[0.08] flex items-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                            <button
                                onClick={() => setBillingCycle('monthly')}
                                className={`px-4 py-1.5 rounded-lg text-xs font-mono font-medium transition-all ${
                                    billingCycle === 'monthly' ? 'bg-zinc-800 text-white font-semibold' : 'text-zinc-400 hover:text-white'
                                }`}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setBillingCycle('annual')}
                                className={`px-4 py-1.5 rounded-lg text-xs font-mono font-medium transition-all flex items-center gap-1.5 ${
                                    billingCycle === 'annual' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold' : 'text-zinc-400 hover:text-white'
                                }`}
                            >
                                <span>Annual Billing</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-300 uppercase font-bold">Save 20%</span>
                            </button>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mb-10 max-w-lg mx-auto bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-xl text-center text-xs font-mono">
                        <p>{error}</p>
                    </div>
                )}

                {/* Plan Cards Grid with Precision Crosshairs */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-20 items-stretch">
                    {plans.map((plan) => {
                        const isLoadingThis = isLoading === planCodes?.[plan.planCodeEnv];
                        const isAnnual = billingCycle === 'annual';
                        
                        const displayPrice = currency === 'USD'
                            ? (isAnnual ? `$${plan.annualPriceUSD}` : `$${plan.monthlyPriceUSD}`)
                            : (isAnnual ? `₦${plan.annualPriceNGN.toLocaleString()}` : `₦${plan.monthlyPriceNGN.toLocaleString()}`);

                        return (
                            <div 
                                key={plan.name}
                                className={`relative flex flex-col rounded-2xl p-7 transition-all duration-300 border ${
                                    plan.isPopular 
                                        ? 'bg-[#0E111B] border-emerald-500/40 shadow-[0_0_60px_rgba(16,185,129,0.08),inset_0_1px_0_0_rgba(255,255,255,0.08)] ring-1 ring-emerald-500/20' 
                                        : 'bg-[#0B0D14] border-white/[0.08] hover:border-white/[0.15] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]'
                                }`}
                            >
                                {/* Precision Architectural Corner Crosshairs */}
                                <div className="absolute -top-1.5 -left-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>
                                <div className="absolute -top-1.5 -right-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>
                                <div className="absolute -bottom-1.5 -left-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>
                                <div className="absolute -bottom-1.5 -right-1.5 font-mono text-[10px] text-zinc-600 pointer-events-none select-none">+</div>

                                {/* Popular / Enterprise Badge */}
                                {plan.badge && (
                                    <div className="mb-4">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                                            <Sparkles className="w-3 h-3" />
                                            {plan.badge}
                                        </span>
                                    </div>
                                )}

                                <div className="mb-6">
                                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                                    <p className="text-xs text-zinc-400 leading-relaxed min-h-[38px]">{plan.description}</p>
                                </div>

                                <div className="flex items-baseline gap-2 mb-6 pb-6 border-b border-white/[0.06]">
                                    <span className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight font-mono">
                                        {displayPrice}
                                    </span>
                                    <span className="text-xs font-mono text-zinc-500">
                                        / month {isAnnual ? '(billed annually)' : ''}
                                    </span>
                                </div>

                                <button
                                    onClick={() => handleSubscribe(plan)}
                                    disabled={!!isLoading}
                                    className={`w-full py-3 px-4 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2 mb-8 ${
                                        plan.isPopular 
                                            ? 'bg-white text-zinc-950 hover:bg-zinc-200 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2)]' 
                                            : 'bg-white/[0.06] text-white hover:bg-white/[0.12] border border-white/[0.08]'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {isLoadingThis ? (
                                        <>
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                            <span>Securing Session...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{plan.cta}</span>
                                            <ArrowRight className="w-3.5 h-3.5" />
                                        </>
                                    )}
                                </button>

                                <div className="space-y-4 flex-1">
                                    <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 font-semibold">Included Specifications</p>
                                    <ul className="space-y-3">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2.5 text-xs text-zinc-300">
                                                <div className="w-4 h-4 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 text-emerald-400">
                                                    <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                                </div>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {plan.missingFeatures && plan.missingFeatures.length > 0 && (
                                        <div className="pt-4 border-t border-white/[0.04] mt-6 space-y-2.5">
                                            {plan.missingFeatures.map((missing, i) => (
                                                <div key={i} className="flex items-start gap-2.5 text-xs text-zinc-500">
                                                    <div className="w-4 h-4 rounded-full bg-zinc-800/60 flex items-center justify-center flex-shrink-0 mt-0.5 text-zinc-600">
                                                        <X className="w-2.5 h-2.5" strokeWidth={3} />
                                                    </div>
                                                    <span>{missing}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Technical Specifications Comparison Matrix */}
                <div className="mb-24">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl font-bold text-white mb-2">Technical Capabilities Breakdown</h2>
                        <p className="text-xs text-zinc-400 max-w-lg mx-auto">Compare detailed architectural limits, Google Sheets synchronizations, and autonomous agent capabilities.</p>
                    </div>

                    <div className="bg-[#0B0D14] border border-white/[0.08] rounded-2xl overflow-hidden shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                                        <th className="p-4 sm:px-6 font-mono uppercase tracking-wider text-zinc-400 font-semibold">Specification</th>
                                        <th className="p-4 text-zinc-300 font-mono font-semibold">Starter</th>
                                        <th className="p-4 text-emerald-400 font-mono font-semibold bg-emerald-500/[0.03]">Pro</th>
                                        <th className="p-4 text-zinc-300 font-mono font-semibold">Advanced</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARISON_ROWS.map((row, idx) => (
                                        <tr key={idx} className="border-b border-white/[0.04] hover:bg-white/[0.01] transition-colors">
                                            <td className="p-4 sm:px-6 text-zinc-300 font-medium">{row.feature}</td>
                                            <td className="p-4 text-zinc-400 font-mono">{row.starter}</td>
                                            <td className="p-4 text-emerald-300 font-mono font-semibold bg-emerald-500/[0.03]">{row.pro}</td>
                                            <td className="p-4 text-zinc-300 font-mono">{row.advanced}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="max-w-3xl mx-auto mb-20">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] text-zinc-400 text-xs font-mono uppercase tracking-wider mb-3">
                            <HelpCircle className="w-3.5 h-3.5 text-emerald-400" />
                            Frequently Asked Questions
                        </div>
                        <h2 className="text-2xl font-bold text-white">Engineering & Infrastructure FAQs</h2>
                    </div>

                    <div className="space-y-3">
                        {FAQS.map((faq, idx) => {
                            const isOpen = expandedFaq === idx;
                            return (
                                <div 
                                    key={idx}
                                    className="bg-[#0B0D14] border border-white/[0.08] rounded-xl overflow-hidden transition-colors"
                                >
                                    <button
                                        onClick={() => toggleFaq(idx)}
                                        className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 focus:outline-none"
                                    >
                                        <span className="text-sm font-semibold text-zinc-200">{faq.q}</span>
                                        {isOpen ? (
                                            <ChevronUp className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                        )}
                                    </button>
                                    {isOpen && (
                                        <div className="px-5 pb-5 text-xs sm:text-sm text-zinc-400 leading-relaxed border-t border-white/[0.04] pt-4">
                                            {faq.a}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Restore Purchases / Subscription Resync */}
                <div className="bg-[#0B0D14] border border-white/[0.08] rounded-2xl p-6 sm:p-8 text-center max-w-2xl mx-auto relative shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
                    <p className="text-xs text-zinc-400 mb-4">Already holding an active enterprise subscription or switched devices?</p>
                    <button 
                        onClick={handleRestorePurchases}
                        disabled={isRestoring}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-xs font-mono text-emerald-400 hover:text-emerald-300 transition-all disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${isRestoring ? 'animate-spin' : ''}`} />
                        <span>Synchronize Workspace State & Purchases</span>
                    </button>
                </div>
            </main>

            {/* Unified Architectural Footer */}
            <footer className="border-t border-white/[0.08] bg-[#07080B] relative z-10 pt-16 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Logo className="h-6 w-auto" />
                            </div>
                            <p className="text-xs text-zinc-400 leading-relaxed mb-4">
                                Official Meta Cloud API infrastructure with autonomous reasoning powered by the latest Gemini models and real-time Google Sheets sync.
                            </p>
                            <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                Meta Tech Provider Partner
                            </div>
                        </div>

                        <div>
                            <p className="text-[11px] font-mono font-semibold uppercase tracking-wider text-zinc-300 mb-4">Navigation</p>
                            <ul className="space-y-2 text-xs text-zinc-400">
                                <li><Link href="/" className="hover:text-white transition-colors">Platform Overview</Link></li>
                                <li><Link href="/pricing" className="text-emerald-400 font-medium">Pricing Plans</Link></li>
                                <li><Link href="/docs" className="hover:text-white transition-colors">Developer Docs</Link></li>
                                <li><Link href="/blog" className="hover:text-white transition-colors">Engineering Playbooks</Link></li>
                            </ul>
                        </div>

                        <div>
                            <p className="text-[11px] font-mono font-semibold uppercase tracking-wider text-zinc-300 mb-4">Integrations</p>
                            <ul className="space-y-2 text-xs text-zinc-400">
                                <li><span className="text-zinc-500">Google Sheets Bidirectional</span></li>
                                <li><span className="text-zinc-500">Latest Gemini Models</span></li>
                                <li><span className="text-zinc-500">Meta WhatsApp Cloud API</span></li>
                                <li><span className="text-zinc-500">Webhook Event Queuing</span></li>
                            </ul>
                        </div>

                        <div>
                            <p className="text-[11px] font-mono font-semibold uppercase tracking-wider text-zinc-300 mb-4">Trust & Compliance</p>
                            <ul className="space-y-2 text-xs text-zinc-400">
                                <li><Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                                <li><Link href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
                                <li><span className="text-zinc-500">Google Limited Use Compliant</span></li>
                                <li><span className="text-zinc-500">End-to-End Encrypted Handshakes</span></li>
                            </ul>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500 font-mono">
                        <p>&copy; {new Date().getFullYear()} Bytecohort Reach. All rights reserved.</p>
                        <p>Meta WhatsApp Business Solution Architecture • Built for High Availability</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
